import { useState, useEffect } from "react";
import { ShieldAlert, Fingerprint, KeyRound, Smartphone, Loader2, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { authClient } from "@/lib/auth-client";
import { toast } from "@/components/ui/sonner";

interface StepUpAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerified: () => void;
  actionType: string;
  reason?: string;
  anomalyScore?: number;
}

type VerificationMethod = 'select' | 'password' | 'passkey' | 'totp';

export const StepUpAuthDialog = ({ 
  open, 
  onOpenChange, 
  onVerified,
  actionType,
  reason,
}: StepUpAuthDialogProps) => {
  const [method, setMethod] = useState<VerificationMethod>('select');
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setMethod('select');
      setPassword("");
      setTotpCode("");
      checkPasskeySupport();
    }
  }, [open]);

  const checkPasskeySupport = async () => {
    try {
      const supported = await authClient.passkey.isSupported();
      setPasskeySupported(supported);
      // Auto-select passkey if supported for better UX
      if (supported) {
        setMethod('passkey');
      }
    } catch (e) {
      setPasskeySupported(false);
    }
  };

  const handlePasswordVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Re-authenticate with password to verify identity
      const { error } = await authClient.signIn.email({
        email: (await authClient.getSession()).data?.user.email || "",
        password: password,
      });

      if (error) throw error;

      toast.success("Identity verified");
      onVerified();
    } catch (error: any) {
      toast.error(error.message || "Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyVerify = async () => {
    setLoading(true);
    try {
      const { error } = await authClient.signIn.passkey();
      if (error) throw error;
      
      toast.success("Identity verified with Passkey");
      onVerified();
    } catch (error: any) {
      console.error(error);
      toast.error("Passkey verification failed. Try password.");
      setMethod('select');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (totpCode.length !== 6) return;
    
    setLoading(true);
    try {
      const { error } = await authClient.twoFactor.verifyTotp({
        code: totpCode,
      });

      if (error) throw error;

      toast.success("Identity verified");
      onVerified();
    } catch (error: any) {
      toast.error(error.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (reason === 'sudo_mode_timeout') return "Sudo Mode Required";
    return "Security Verification";
  };

  const getDescription = () => {
    if (reason === 'sudo_mode_timeout') return "Your session is too old for this sensitive action. Please re-verify your identity.";
    return `Please verify your identity to continue with: ${actionType.replace(/_/g, ' ')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" />
            {getTitle()}
          </DialogTitle>
          <DialogDescription>
            {getDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Method Selection */}
          {method === 'select' && (
            <div className="grid gap-3">
              {passkeySupported && (
                <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => setMethod('passkey')}>
                  <Fingerprint className="h-5 w-5 mr-3 text-primary" />
                  <div className="text-left">
                    <div className="font-semibold">Use Passkey</div>
                    <div className="text-xs text-muted-foreground">Fastest method (FaceID / TouchID)</div>
                  </div>
                </Button>
              )}
              
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => setMethod('password')}>
                <KeyRound className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Use Password</div>
                  <div className="text-xs text-muted-foreground">Enter your account password</div>
                </div>
              </Button>

              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => setMethod('totp')}>
                <Smartphone className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Use Authenticator App</div>
                  <div className="text-xs text-muted-foreground">Enter a 6-digit TOTP code</div>
                </div>
              </Button>
            </div>
          )}

          {/* Passkey Flow */}
          {method === 'passkey' && (
            <div className="text-center py-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Fingerprint className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">
                Waiting for your device...
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="ghost" onClick={() => setMethod('select')}>Use another method</Button>
                <Button onClick={handlePasskeyVerify} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Try Again"}
                </Button>
              </div>
              {/* Auto-trigger passkey on mount if selected */}
              <PasskeyAutoTrigger trigger={handlePasskeyVerify} />
            </div>
          )}

          {/* Password Flow */}
          {method === 'password' && (
            <form onSubmit={handlePasswordVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sudo-password">Password</Label>
                <Input
                  id="sudo-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={() => setMethod('select')} className="flex-1">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button type="submit" disabled={!password || loading} className="flex-1">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                </Button>
              </div>
            </form>
          )}

          {/* TOTP Flow */}
          {method === 'totp' && (
            <form onSubmit={handleTotpVerify} className="space-y-4">
               <div className="space-y-2">
                <Label htmlFor="totp-code">Verification Code</Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={totpCode}
                    onChange={(value) => setTotpCode(value)}
                    disabled={loading}
                    autoFocus
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={() => setMethod('select')} className="flex-1">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button type="submit" disabled={totpCode.length !== 6 || loading} className="flex-1">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper to trigger async function once on mount
const PasskeyAutoTrigger = ({ trigger }: { trigger: () => void }) => {
  useEffect(() => {
    trigger();
  }, []);
  return null;
};
